import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Navbar } from '../components/Navbar';
import { AdBanner, useShowAds } from '../components/AdBanner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { 
    User, 
    Mail, 
    Phone, 
    Crown, 
    Moon, 
    Sun,
    Shield,
    Bell
} from 'lucide-react';

export default function SettingsPage() {
    const { user, isPremium } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const [notifications, setNotifications] = useState(true);
    const showAds = useShowAds(isPremium);

    const handleSaveProfile = (e) => {
        e.preventDefault();
        toast.success('Profile settings saved');
    };

    return (
        <div className="min-h-screen bg-background">
            <Navbar />
            
            <div className="pt-24 pb-16 px-6">
                <div className="max-w-2xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        <h1 className="font-heading text-3xl font-semibold text-foreground mb-2">
                            Settings
                        </h1>
                        <p className="text-muted-foreground mb-8">
                            Manage your account and preferences
                        </p>

                        {/* Profile Section */}
                        <Card className="mb-6">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <User className="w-5 h-5" />
                                    Profile Information
                                </CardTitle>
                                <CardDescription>
                                    Your personal information
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <form onSubmit={handleSaveProfile} className="space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="name">Name</Label>
                                        <Input 
                                            id="name" 
                                            defaultValue={user?.name} 
                                            data-testid="settings-name"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="email">Email</Label>
                                        <div className="relative">
                                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                            <Input 
                                                id="email" 
                                                type="email"
                                                defaultValue={user?.email}
                                                className="pl-10"
                                                disabled
                                            />
                                        </div>
                                        <p className="text-xs text-muted-foreground">Email cannot be changed</p>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="phone">Phone</Label>
                                        <div className="relative">
                                            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                            <Input 
                                                id="phone" 
                                                type="tel"
                                                defaultValue={user?.phone}
                                                className="pl-10"
                                                disabled
                                            />
                                        </div>
                                    </div>
                                    <Button type="submit" data-testid="save-profile">
                                        Save Changes
                                    </Button>
                                </form>
                            </CardContent>
                        </Card>

                        {/* Subscription Section */}
                        <Card className="mb-6">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Crown className="w-5 h-5" />
                                    Subscription
                                </CardTitle>
                                <CardDescription>
                                    Your current plan and billing
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="flex items-center justify-between p-4 rounded-lg bg-muted">
                                    <div>
                                        <p className="font-semibold text-foreground">
                                            {isPremium ? 'Ustadh Mode' : 'Free Plan'}
                                        </p>
                                        <p className="text-sm text-muted-foreground">
                                            {isPremium 
                                                ? 'Full access to voice features and personalized learning'
                                                : 'Basic Islamic Q&A access'}
                                        </p>
                                    </div>
                                    {isPremium ? (
                                        <span className="px-3 py-1 text-sm rounded-full bg-secondary/10 text-secondary border border-secondary/20">
                                            Active
                                        </span>
                                    ) : (
                                        <Button 
                                            variant="outline" 
                                            className="border-secondary text-secondary"
                                            onClick={() => window.location.href = '/subscription'}
                                            data-testid="upgrade-from-settings"
                                        >
                                            Upgrade
                                        </Button>
                                    )}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Preferences Section */}
                        <Card className="mb-6">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Shield className="w-5 h-5" />
                                    Preferences
                                </CardTitle>
                                <CardDescription>
                                    Customize your experience
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        {theme === 'dark' ? (
                                            <Moon className="w-5 h-5 text-muted-foreground" />
                                        ) : (
                                            <Sun className="w-5 h-5 text-muted-foreground" />
                                        )}
                                        <div>
                                            <p className="font-medium text-foreground">Dark Mode</p>
                                            <p className="text-sm text-muted-foreground">
                                                Toggle between light and dark themes
                                            </p>
                                        </div>
                                    </div>
                                    <Switch 
                                        checked={theme === 'dark'}
                                        onCheckedChange={toggleTheme}
                                        data-testid="dark-mode-toggle"
                                    />
                                </div>
                                
                                <Separator />
                                
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <Bell className="w-5 h-5 text-muted-foreground" />
                                        <div>
                                            <p className="font-medium text-foreground">Notifications</p>
                                            <p className="text-sm text-muted-foreground">
                                                Receive updates and reminders
                                            </p>
                                        </div>
                                    </div>
                                    <Switch 
                                        checked={notifications}
                                        onCheckedChange={setNotifications}
                                        data-testid="notifications-toggle"
                                    />
                                </div>
                            </CardContent>
                        </Card>

                        {/* Danger Zone */}
                        <Card className="border-destructive/50">
                            <CardHeader>
                                <CardTitle className="text-destructive">Danger Zone</CardTitle>
                                <CardDescription>
                                    Irreversible actions
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <Button 
                                    variant="destructive" 
                                    onClick={() => toast.error('Account deletion is not available in demo mode')}
                                    data-testid="delete-account"
                                >
                                    Delete Account
                                </Button>
                            </CardContent>
                        </Card>

                        {/* Inline Ad for Free Users */}
                        {showAds && (
                            <div className="mt-6">
                                <AdBanner placement="inline" />
                            </div>
                        )}
                    </motion.div>
                </div>
            </div>

            {/* Bottom Banner Ad for Free Users */}
            {showAds && <AdBanner placement="banner" />}
        </div>
    );
}
